import { CommanServices } from "@/services/commanServices";
import { GoogleMap, Marker, InfoWindow, Polyline, useJsApiLoader } from "@react-google-maps/api";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { createPortal } from "react-dom";
import { Info, CloudSun, Wind, Biohazard, Volume2, Pencil, Plus, X, Trash2 } from "lucide-react";
import IncidentWeatherChip from "./IncidentWeatherChip";
import { useIncidentPersonnel, PersonnelMarkers } from "./map/IncidentPersonnel";
import { MapGisOverlay, usePersistentGisMap, MapStyleSelector, buildMapStyleOptions, GOOGLE_MAPS_LOADER_OPTIONS } from "./map/MapGisTools";
import { buildZoneIndex, resolveIncidentCoords, resolveZoneCoords } from "./map/zoneCoords";
import { useMapsUnavailable, MapUnavailable } from "./map/MapUnavailable";

const containerStyle = { width: "100%", height: "420px" };
const FALLBACK_CENTER = { lat: 24.7136, lng: 46.6753 }; // used only until Operations Area loads

const EMPTY_INFO = { weather: "", wind: "", gases: "", loudSound: "", custom: [] };
const hasAnyInfo = (info) => !!(info && (info.weather || info.wind || info.gases || info.loudSound || (info.custom || []).some((c) => c.label || c.value)));

const spreadFamilyMarkers = (markers, selectedId) => {
  const groups = new Map();
  markers.forEach((marker) => {
    const key = `${Number(marker.lat).toFixed(5)}:${Number(marker.lng).toFixed(5)}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(marker);
  });
  return markers.map((marker) => {
    const key = `${Number(marker.lat).toFixed(5)}:${Number(marker.lng).toFixed(5)}`;
    const group = [...(groups.get(key) || [])].sort((a, b) => (a.id === selectedId ? -1 : b.id === selectedId ? 1 : String(a.id).localeCompare(String(b.id))));
    const index = group.findIndex((entry) => entry.id === marker.id);
    if (index <= 0) return marker;
    const angle = ((index - 1) % 8) * (Math.PI / 4);
    const radius = 0.000055 * Math.ceil(index / 8);
    return { ...marker, lat: Number(marker.lat) + Math.cos(angle) * radius, lng: Number(marker.lng) + Math.sin(angle) * radius };
  });
};

export default function MyMap({ incident = {}, trackedUser, isAdmin = false, generalInfoOpenRequest = 0 }) {
  const [locations, setLocations] = useState([]);
  const [markerPins, setMarkerPins] = useState([]);
  const [familyEntries, setFamilyEntries] = useState([]);
  const [operationsCenter, setOperationsCenter] = useState(FALLBACK_CENTER);
  const [center, setcenter] = useState(FALLBACK_CENTER);
  const [map, setMap] = useState(null);

  const { isLoaded } = useJsApiLoader(GOOGLE_MAPS_LOADER_OPTIONS);
  const mapsUnavailable = useMapsUnavailable();

  // Saved GIS map (read-only PREVIEW) — drawings are created/edited on the full map
  const { tasks, zones, points, setTasks, setPoints, mapInfo, setMapInfo } = usePersistentGisMap(incident?.id, !!incident?.id, isAdmin);
  const [heatmapOn] = useState(false);
  const [sequenceOn] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [mapStyle, setMapStyle] = useState('standard');

  // General information shown on the incident location marker.
  // Admins edit it right here through the Location-Info modal.
  const [infoOpen, setInfoOpen] = useState(false);
  const [selectedRelatedId, setSelectedRelatedId] = useState(null);
  const [selectedRelationId, setSelectedRelationId] = useState(null);
  const [editInfo, setEditInfo] = useState(null);
  const handledGeneralInfoRequest = useRef(0);
  const saveMapInfo = (info) => {
    setMapInfo(info); // usePersistentGisMap auto-saves the GIS payload
    setEditInfo(null);
  };

  useEffect(() => {
    if (!isAdmin || !generalInfoOpenRequest || handledGeneralInfoRequest.current === generalInfoOpenRequest) return;
    handledGeneralInfoRequest.current = generalInfoOpenRequest;
    setEditInfo({ ...EMPTY_INFO, ...(mapInfo || {}) });
  }, [generalInfoOpenRequest, isAdmin, mapInfo]);

  // ── Assigned personnel — everyone staffed on this incident (assigned_users,
  // role assignments, manual section additions) with their live position.
  // Shared layer with the Full/Create Map page so both stay identical.
  const personnel = useIncidentPersonnel(incident?.id);
  const personnelPins = personnel.filter((p) => p.lat != null && p.lng != null);

  const incidentTasks = tasks;
  const incidentZones = zones;

  const fetchLocations = async () => {
    try {
      const [response, operationsAreaResponse] = await Promise.all([
        CommanServices.getlocationszonesdata(),
        CommanServices.getOperationsArea(),
      ]);
      if (response.status) setLocations((await response.data) || []);
      const operationsPoint = operationsAreaResponse?.data?.center;
      const operationsLat = Number(operationsPoint?.latitude ?? operationsPoint?.lat);
      const operationsLng = Number(operationsPoint?.longitude ?? operationsPoint?.lng);
      if (Number.isFinite(operationsLat) && Number.isFinite(operationsLng)) {
        const nextCenter = { lat: operationsLat, lng: operationsLng };
        setOperationsCenter(nextCenter);
        if (!trackedUser && markerPins.length === 0) setcenter(nextCenter);
      }
    } catch (err) { console.error("❌ MyMap locations:", err); }
  };
  useEffect(() => { fetchLocations(); }, []);

  useEffect(() => {
    let cancelled = false;
    if (!incident?.id) {
      setFamilyEntries([]);
      return undefined;
    }
    (async () => {
      try {
        const response = await CommanServices.getincidentsFamily(incident.id);
        const payload = response?.data ?? response ?? {};
        if (!cancelled) setFamilyEntries(Array.isArray(payload?.family_tree) ? payload.family_tree : []);
      } catch {
        if (!cancelled) setFamilyEntries([]);
      }
    })();
    return () => { cancelled = true; };
  }, [incident?.id]);

  useEffect(() => {
    try {
      let setpins = [];
      const zoneIndex = buildZoneIndex(locations);
      const canonicalPoint = resolveIncidentCoords(zoneIndex, incident);
      if (incident?.map_projection && canonicalPoint) {
        setpins.push({ lat: canonicalPoint.lat, lng: canonicalPoint.lng });
      }
      // The SELECTED location (zone chosen in the report) always wins — the
      // reporter's device GPS is only a fallback for Unknown-location reports.
      // (It used to be the other way around, so the map centered on wherever
      // the reporter happened to stand instead of the reported location.)
      const locIds = (Array.isArray(incident?.location) ? incident.location : [incident?.location]).filter(Boolean);
      if (!incident?.map_projection) {
        locIds.forEach((id) => {
          const c = resolveZoneCoords(zoneIndex, id);
          if (c) setpins.push({ lat: c.lat, lng: c.lng });
        });
      }
      if (setpins.length === 0) {
        const gps = incident?.gps_coordinates;
        const incidentLat = gps?.latitude ?? gps?.lat ?? incident?.gps_latitude;
        const incidentLng = gps?.longitude ?? gps?.lng ?? incident?.gps_longitude;
        const hasIncidentGps = incidentLat != null && incidentLng != null && !Number.isNaN(Number(incidentLat)) && !Number.isNaN(Number(incidentLng));
        if (hasIncidentGps) {
          setpins.push({ lat: Number(incidentLat), lng: Number(incidentLng) });
        }
      }
      if (setpins.length > 0) {
        if (!trackedUser) setcenter(setpins[0]);
        setMarkerPins(setpins);
      } else {
        setMarkerPins([]);
        if (!trackedUser) setcenter(operationsCenter);
      }
    } catch (error) { console.error(error); }
  }, [JSON.stringify(locations), JSON.stringify(incident?.location), JSON.stringify(incident?.gps_coordinates), JSON.stringify(incident?.map_projection), incident?.gps_latitude, incident?.gps_longitude, !!trackedUser, operationsCenter]);

  const familyMarkers = useMemo(() => {
    if (!familyEntries.length) return [];
    const zoneIndex = buildZoneIndex(locations);
    const markers = familyEntries.map((entry) => {
      const raw = String(entry?.incident?.id) === String(incident?.id)
        ? { ...entry.incident, ...incident }
        : entry?.incident;
      if (!raw?.id) return null;
      const point = resolveIncidentCoords(zoneIndex, raw);
      if (!point) return null;
      return {
        ...raw,
        id: String(raw.id),
        lat: Number(point.lat),
        lng: Number(point.lng),
        zone_name: point.name || point.matchedName || '',
        parent_id: entry?.parent_id || raw.parent_incident_id || null,
      };
    }).filter(Boolean);
    return spreadFamilyMarkers(markers, String(incident?.id));
  }, [familyEntries, incident, locations]);

  const familyById = useMemo(() => new Map(familyMarkers.map((entry) => [String(entry.id), entry])), [familyMarkers]);
  const familyRelations = useMemo(() => familyMarkers.flatMap((child) => {
    const parent = familyById.get(String(child.parent_id || child.parent_incident_id || ''));
    if (!parent) return [];
    return [{
      id: `${parent.id}-${child.id}`,
      parent,
      child,
      midpoint: { lat: (Number(parent.lat) + Number(child.lat)) / 2, lng: (Number(parent.lng) + Number(child.lng)) / 2 },
      path: [{ lat: Number(parent.lat), lng: Number(parent.lng) }, { lat: Number(child.lat), lng: Number(child.lng) }],
    }];
  }), [familyById, familyMarkers]);

  useEffect(() => {
    if (!map || familyMarkers.length < 2 || !window.google?.maps || trackedUser) return;
    const bounds = new window.google.maps.LatLngBounds();
    familyMarkers.forEach((entry) => bounds.extend({ lat: Number(entry.lat), lng: Number(entry.lng) }));
    map.fitBounds(bounds, 48);
    const listener = window.google.maps.event.addListenerOnce(map, 'idle', () => {
      if (map.getZoom() > 17) map.setZoom(17);
    });
    return () => window.google.maps.event.removeListener(listener);
  }, [familyMarkers, map, trackedUser]);

  useEffect(() => {
    if (trackedUser && trackedUser.lat && trackedUser.lng) setcenter({ lat: Number(trackedUser.lat), lng: Number(trackedUser.lng) });
  }, [trackedUser]);

  // Apply the selected map style preset.
  useEffect(() => {
    if (map) {
      const styleOptions = buildMapStyleOptions(mapStyle, { mapTypeControl: false });
      if (incident?.map_projection && !incident.map_projection.official) {
        styleOptions.styles = [
          ...(styleOptions.styles || []),
          { elementType: 'geometry', stylers: [{ saturation: -100 }, { lightness: 12 }] },
          { elementType: 'labels.icon', stylers: [{ saturation: -100 }] },
        ];
      }
      map.setOptions(styleOptions);
    }
  }, [incident?.map_projection, map, mapStyle]);

  if (mapsUnavailable) {
    return <div style={containerStyle} className="rounded-lg overflow-hidden"><MapUnavailable center={center} setGpsHref="/locations-zones" /></div>;
  }

  if (!isLoaded) {
    return <div style={containerStyle} className="flex items-center justify-center bg-gray-100 text-gray-400 text-sm rounded-lg">Loading map…</div>;
  }

  return (
    <div className="relative">
      {/* Read-only preview header: map style + a small legend. Drawing happens on the full map. */}
      <div className="absolute top-3 left-3 z-10 flex flex-wrap items-center gap-2" data-testid="incident-map-toolbar">
        <MapStyleSelector value={mapStyle} onChange={setMapStyle} testid="incident-map-style" />
        <span className="text-[10px] font-bold text-gray-500 bg-white/95 border border-gray-200 rounded-lg px-2 py-1.5 shadow-sm">Preview · read-only</span>
        {isAdmin && (
          <button
            data-testid="incident-general-info"
            onClick={() => setEditInfo({ ...EMPTY_INFO, ...(mapInfo || {}) })}
            className="flex items-center gap-1 text-[10px] font-bold text-indigo-600 bg-white/95 border border-gray-200 rounded-lg px-2 py-1.5 shadow-sm hover:bg-indigo-50"
          >
            <Info className="w-3.5 h-3.5" /> General Info
          </button>
        )}
      </div>

      {/* Task / point / personnel counts */}
      <div className="absolute top-3 right-3 z-10 bg-white/95 backdrop-blur border border-gray-200 rounded-lg shadow-sm px-3 py-2" data-testid="incident-map-legend">
        <div className="flex items-center gap-3">
          <span className="text-[10px] font-bold text-gray-500">Tasks: {incidentTasks.length}</span>
          <span className="text-[10px] font-bold text-gray-500">Points: {points.length}</span>
          {familyMarkers.length > 1 && <span className="text-[10px] font-bold text-purple-600">Related: {familyMarkers.length - 1}</span>}
          {personnel.length > 0 && (
            <span className="text-[10px] font-bold text-emerald-600" data-testid="incident-map-personnel-count">
              Personnel: {personnelPins.length}/{personnel.length}
            </span>
          )}
        </div>
      </div>

      <GoogleMap
        mapContainerStyle={containerStyle}
        center={center}
        zoom={14}
        onLoad={setMap}
        options={buildMapStyleOptions(mapStyle, { mapTypeControl: false })}
      >
        {familyRelations.map((relation) => {
          const selected = selectedRelationId === relation.id;
          return (
            <Fragment key={relation.id}>
              <Polyline
                path={relation.path}
                onClick={() => setSelectedRelationId(relation.id)}
                options={{
                  strokeColor: selected ? '#f97316' : '#a855f7',
                  strokeOpacity: 0,
                  strokeWeight: 14,
                  clickable: true,
                  geodesic: true,
                  zIndex: selected ? 90 : 55,
                  icons: [{
                    icon: { path: 'M 0,-1 0,1', strokeColor: selected ? '#f97316' : '#a855f7', strokeOpacity: 1, strokeWeight: selected ? 3 : 2, scale: 3 },
                    offset: '0',
                    repeat: '18px',
                  }],
                }}
              />
              {selected && (
                <InfoWindow position={relation.midpoint} onCloseClick={() => setSelectedRelationId(null)}>
                  <div className="min-w-[220px] p-1" data-testid={`incident-widget-relation-${relation.id}`}>
                    <p className="text-[10px] font-bold uppercase tracking-wider text-purple-600">Linked incidents</p>
                    <p className="mt-1 text-xs font-semibold text-gray-800">Root/parent: {relation.parent.id}</p>
                    <p className="text-xs font-semibold text-gray-800">Related: {relation.child.id}</p>
                  </div>
                </InfoWindow>
              )}
            </Fragment>
          );
        })}

        {markerPins.map((item, i) => (
          <Marker
            key={`pin-${i}`}
            position={item}
            onClick={() => i === 0 && setInfoOpen(true)}
            icon={i === 0 && incident?.map_projection && !incident.map_projection.official ? {
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 9,
              fillColor: '#64748b',
              fillOpacity: 1,
              strokeColor: '#f8fafc',
              strokeWeight: 3,
            } : undefined}
          >
            {i === 0 && infoOpen && (
              <InfoWindow position={item} onCloseClick={() => setInfoOpen(false)}>
                <div className="min-w-[230px] max-w-[280px] p-1" data-testid="incident-info-window">
                  <p className="text-sm font-bold text-gray-900 mb-2 flex items-center gap-1.5">
                    <Info className="w-4 h-4 text-indigo-600" /> Incident Location Info
                  </p>
                  {/* On-scene weather straight from the registered weather API */}
                  <div className="mb-2">
                    <IncidentWeatherChip incidentId={incident?.id} />
                  </div>
                  {hasAnyInfo(mapInfo) ? (
                    <div className="space-y-1.5">
                      {mapInfo.weather && <InfoRow icon={<CloudSun className="w-3.5 h-3.5 text-amber-500" />} label="Weather" value={mapInfo.weather} />}
                      {mapInfo.wind && <InfoRow icon={<Wind className="w-3.5 h-3.5 text-sky-500" />} label="Wind direction" value={mapInfo.wind} />}
                      {mapInfo.gases && <InfoRow icon={<Biohazard className="w-3.5 h-3.5 text-green-600" />} label="Gases" value={mapInfo.gases} />}
                      {mapInfo.loudSound && <InfoRow icon={<Volume2 className="w-3.5 h-3.5 text-rose-500" />} label="Loud sound" value={mapInfo.loudSound} />}
                      {(mapInfo.custom || []).filter((c) => c.label || c.value).map((c, idx) => (
                        <InfoRow key={idx} icon={<Info className="w-3.5 h-3.5 text-gray-400" />} label={c.label || "Custom"} value={c.value} />
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-500">
                      No general information added yet.
                      {isAdmin ? " Add weather, wind direction, gases and more so everyone on this incident shares the same picture." : ""}
                    </p>
                  )}
                  {isAdmin && (
                    <button
                      data-testid="info-window-edit"
                      onClick={() => { setEditInfo({ ...EMPTY_INFO, ...(mapInfo || {}) }); setInfoOpen(false); }}
                      className="mt-2.5 flex items-center gap-1 text-[11px] font-bold text-indigo-600 hover:underline"
                    >
                      <Pencil className="w-3 h-3" /> {hasAnyInfo(mapInfo) ? "Edit info" : "Add info"}
                    </button>
                  )}
                </div>
              </InfoWindow>
            )}
          </Marker>
        ))}

        {familyMarkers.filter((entry) => String(entry.id) !== String(incident?.id)).map((related) => (
          <Marker
            key={`related-${related.id}`}
            position={{ lat: Number(related.lat), lng: Number(related.lng) }}
            onClick={() => setSelectedRelatedId(String(related.id))}
            zIndex={150}
            label={{ text: 'R', color: '#ffffff', fontSize: '10px', fontWeight: '700' }}
            icon={{ path: window.google.maps.SymbolPath.CIRCLE, scale: 9, fillColor: '#0891b2', fillOpacity: 1, strokeColor: '#ffffff', strokeWeight: 2 }}
          >
            {selectedRelatedId === String(related.id) && (
              <InfoWindow onCloseClick={() => setSelectedRelatedId(null)}>
                <div className="min-w-[230px] max-w-[280px] p-1" data-testid={`incident-widget-related-${related.id}`}>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-cyan-700">Related incident</p>
                  <p className="mt-1 text-sm font-bold text-gray-900">{related.incident_type || related.title || 'Incident'}</p>
                  <p className="mt-0.5 text-[11px] font-semibold text-gray-500">{related.id}</p>
                  {related.zone_name && <p className="mt-1 text-xs text-gray-600">{related.zone_name}</p>}
                  <Link to={`/incident-details/${related.id}`} className="mt-2 inline-block text-[11px] font-bold text-indigo-600 hover:underline">Open incident</Link>
                </div>
              </InfoWindow>
            )}
          </Marker>
        ))}
        {trackedUser && trackedUser.lat && trackedUser.lng && (
          <Marker position={{ lat: Number(trackedUser.lat), lng: Number(trackedUser.lng) }}
            icon={{ path: window.google.maps.SymbolPath.CIRCLE, scale: 9, fillColor: "#2563eb", fillOpacity: 1, strokeColor: "#fff", strokeWeight: 2 }} />
        )}

        {/* Assigned personnel — live green dots, click for who/role/last-seen */}
        <PersonnelMarkers personnel={personnel} />

        <MapGisOverlay
          tasks={incidentTasks} setTasks={setTasks}
          zones={incidentZones} setZones={() => {}}
          points={points} setPoints={setPoints}
          drawShape="none" setDrawShape={() => {}}
          heatmapOn={heatmapOn} sequenceOn={sequenceOn}
          activeImportance="response"
          selectedTaskId={selectedTaskId} setSelectedTaskId={setSelectedTaskId}
          incidents={familyMarkers.length ? familyMarkers : [incident]}
          incidentId={incident?.id}
          readOnly
        />
      </GoogleMap>

      {editInfo && <GeneralInfoModal info={editInfo} onSave={saveMapInfo} onClose={() => setEditInfo(null)} />}
    </div>
  );
}

const InfoRow = ({ icon, label, value }) => (  <div className="flex items-start gap-1.5 text-xs">
    <span className="mt-0.5 shrink-0">{icon}</span>
    <span className="text-gray-500 font-semibold">{label}:</span>
    <span className="text-gray-800 break-words">{value}</span>
  </div>
);

const InfoField = ({ icon, label, k, value, onChange, placeholder }) => (
  <div>
    <label className="text-[10px] font-bold uppercase tracking-wider text-gray-400 flex items-center gap-1.5">{icon}{label}</label>
    <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={`mapinfo-${k}`}
      className="w-full text-sm border border-gray-300 rounded-md text-gray-900 bg-white px-2.5 py-2 mt-1" />
  </div>
);

// Location-Info modal — general awareness fields shown on the incident marker.
// Saved into the incident's persisted GIS payload (server-side, shared with
// every viewer of the map), for real incidents and requested-stage exercises.
const GeneralInfoModal = ({ info, onSave, onClose }) => {
  const [form, setForm] = useState(info);
  useEffect(() => { setForm(info); }, [info]);
  if (!info) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setCustom = (i, k, v) => setForm((f) => ({ ...f, custom: (f.custom || []).map((c, idx) => idx === i ? { ...c, [k]: v } : c) }));
  const addCustom = () => setForm((f) => ({ ...f, custom: [...(f.custom || []), { label: "", value: "" }] }));
  const removeCustom = (i) => setForm((f) => ({ ...f, custom: (f.custom || []).filter((_, idx) => idx !== i) }));

  return createPortal(
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/40 p-4" data-testid="mapinfo-modal">
      <div className="bg-white rounded-xl shadow-2xl w-[440px] max-h-[90vh] overflow-y-auto p-5 space-y-3.5">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-bold text-gray-900 flex items-center gap-2"><Info className="w-4 h-4 text-indigo-600" /> Location general information</h3>
          <button onClick={onClose} data-testid="mapinfo-close"><X className="w-4 h-4 text-gray-400" /></button>
        </div>
        <p className="text-[11px] text-gray-500 -mt-1">Shown to everyone when the incident location marker is clicked — keep it current to raise shared awareness.</p>

        <InfoField icon={<CloudSun className="w-3.5 h-3.5 text-amber-500" />} label="Weather" k="weather" value={form.weather} onChange={(v) => set("weather", v)} placeholder="e.g. Clear, 32°C, humid" />
        <InfoField icon={<Wind className="w-3.5 h-3.5 text-sky-500" />} label="Wind direction" k="wind" value={form.wind} onChange={(v) => set("wind", v)} placeholder="e.g. North-East, 18 km/h" />
        <InfoField icon={<Biohazard className="w-3.5 h-3.5 text-green-600" />} label="Gases" k="gases" value={form.gases} onChange={(v) => set("gases", v)} placeholder="e.g. Chlorine leak detected" />
        <InfoField icon={<Volume2 className="w-3.5 h-3.5 text-rose-500" />} label="Loud sound" k="loudSound" value={form.loudSound} onChange={(v) => set("loudSound", v)} placeholder="e.g. Explosion / alarm at 95 dB" />

        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Custom fields</label>
            <button onClick={addCustom} data-testid="mapinfo-add-custom" className="flex items-center gap-1 text-[11px] font-bold text-indigo-600 hover:underline"><Plus className="w-3 h-3" /> Add</button>
          </div>
          <div className="space-y-2">
            {(form.custom || []).map((c, i) => (
              <div key={i} className="flex items-center gap-2" data-testid={`mapinfo-custom-${i}`}>
                <input value={c.label} onChange={(e) => setCustom(i, "label", e.target.value)} placeholder="Label" className="w-1/3 text-xs border border-gray-300 rounded-md text-gray-900 bg-white px-2 py-1.5" />
                <input value={c.value} onChange={(e) => setCustom(i, "value", e.target.value)} placeholder="Value" className="flex-1 text-xs border border-gray-300 rounded-md text-gray-900 bg-white px-2 py-1.5" />
                <button onClick={() => removeCustom(i)} className="p-1 text-gray-400 hover:text-rose-600"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            ))}
            {(form.custom || []).length === 0 && <p className="text-[11px] text-gray-400">No custom fields. Click “Add” to create one.</p>}
          </div>
        </div>

        <button onClick={() => onSave(form)} data-testid="mapinfo-save"
          className="w-full bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-bold py-2.5 rounded-md transition-colors">
          Save information
        </button>
      </div>
    </div>,
    document.body
  );
};
